import { V4, debug } from '@moleculeone/utils';

import { mergePositions } from './utils';
import { positionsDataTest } from '../checks';

const padding = V4.vect(50, 50);

function packBoxes(boxSizes, maxTotalSize) {
  let availableCorners = [V4.vect(0, 0)];
  const addBox = newBoxSize => {
    let chosenCornerId = 0;
    let newBoxCorner = null;
    let newBoxTopRight = null;
    while (chosenCornerId < availableCorners.length) {
      newBoxCorner = availableCorners[chosenCornerId];
      newBoxTopRight = V4.add(newBoxCorner, newBoxSize);
      if (newBoxTopRight[0] <= maxTotalSize[0] && newBoxTopRight[1] <= maxTotalSize[1]) {
        // it fits, we'll place new box in the chosen corner
        break;
      }
      chosenCornerId += 1;
    }

    if (chosenCornerId === availableCorners.length) {
      // there's no place for the new box
      return null;
    }

    // recalculate corners where new boxes may be put
    let lastCornerAboveNewBox = null;
    let lastCornerIdx = 0;
    while (lastCornerIdx < availableCorners.length) {
      lastCornerAboveNewBox = availableCorners[lastCornerIdx];
      if (lastCornerAboveNewBox[1] <= newBoxTopRight[1]) {
        break;
      }
      lastCornerIdx += 1;
    }

    let firstCornerOnRightToNewBox = null;
    let firstCornerIdx = availableCorners.length - 1;
    while (firstCornerIdx >= 0) {
      firstCornerOnRightToNewBox = availableCorners[firstCornerIdx];
      if (firstCornerOnRightToNewBox[0] <= newBoxTopRight[0]) {
        break;
      }
      firstCornerIdx -= 1;
    }

    const newLastCornerAbove = [lastCornerAboveNewBox[0], newBoxTopRight[1], 0];
    const newFirstCornerOnRight = [newBoxTopRight[0], firstCornerOnRightToNewBox[1], 0];
    availableCorners = [
      ...availableCorners.slice(0, lastCornerIdx),
      newLastCornerAbove,
      newFirstCornerOnRight,
      ...availableCorners.slice(firstCornerIdx + 1, availableCorners.length),
    ];
    return newBoxCorner;
  };

  const boxBottomLefts = {};
  const boxSizesList = Object.entries(boxSizes);
  boxSizesList.sort(([, b1], [, b2]) => b2[0] * b2[1] - b1[0] * b1[1]); // descending box area order
  for (const [groupId, groupBoxSize] of boxSizesList) {
    const bottomLeftCorner = addBox(groupBoxSize);
    if (bottomLeftCorner == null) {
      return null;
    }
    boxBottomLefts[groupId] = bottomLeftCorner;
  }
  return boxBottomLefts;
}

function boundingBoxSize(groupPositions) {
  const positions = Object.values(groupPositions);
  const min = V4.min(...positions);
  const max = V4.max(...positions);
  return V4.sub(max, min);
}

export function mergeComponents(positionsData) {
  const groupPositions = Object.entries(positionsData.groupPositions);
  const groupBoxSizes = {};
  for (const [key, group] of groupPositions) {
    groupBoxSizes[key] = V4.add(padding, boundingBoxSize(group), padding);
  }
  const boxSizesList = Object.values(groupBoxSizes);
  // Each group (disjoint graph component) is represented by a box with padding.
  // we will try to pack those boxes in a square,
  // and we will find optimal square size via binsearch.
  let maxTotalSize = V4.add(...boxSizesList);
  let minTotalSize = V4.min(...boxSizesList);
  let currentBoxPacking = null;
  const BINSEARCH_ITERS = 6;
  for (let i = 0; i < BINSEARCH_ITERS; i++) {
    const midTotalSize = V4.average(maxTotalSize, minTotalSize);
    currentBoxPacking = packBoxes(groupBoxSizes, midTotalSize);
    if (currentBoxPacking == null) {
      minTotalSize = midTotalSize;
    } else {
      maxTotalSize = midTotalSize;
    }
  }
  if (currentBoxPacking == null) {
    currentBoxPacking = packBoxes(groupBoxSizes, maxTotalSize);
  }

  for (const [groupIdx, bottomLeftBoxPos] of Object.entries(currentBoxPacking)) {
    const currentPositions = positionsData.groupPositions[groupIdx];
    const packedPosition = {};
    const bottomLeftPos = V4.min(...Object.values(currentPositions));
    for (const [atomIdx, atomPos] of Object.entries(currentPositions)) {
      packedPosition[atomIdx] = V4.sub(V4.add(bottomLeftBoxPos, padding, atomPos), bottomLeftPos);
    }
    positionsData.groupPositions[groupIdx] = packedPosition;
  }

  positionsData.result = mergePositions(...Object.values(positionsData.groupPositions));
  debug(positionsDataTest, positionsData);
}
